import { alpha } from '@mui/material/styles';

function parseHex(c) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(c || '');
  return m
    ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)]
    : null;
}

// Perceived-luminance test — `getColor` produces light pastels (150–255) meant
// as backgrounds, which are invisible as text on a faint background.
function isLightColor(c) {
  const rgb = parseHex(c);
  if (!rgb) return false;
  const lum = 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2];
  return lum > 185;
}

/**
 * Tag-chip tone. Adaptive so it reads well for ANY colour:
 *  - saturated/dark colour → the colour as TEXT over a very-low-opacity bg of
 *    the same colour (the liked "Preclinic"/"Imported" look).
 *  - light pastel colour → the colour as a solid BACKGROUND with dark text
 *    (otherwise colour-as-text would be unreadable).
 */
export function tagChipStyle(color, extra = {}) {
  const c = color || '#5b6b7c';
  if (isLightColor(c)) {
    return {
      backgroundColor: c,
      color: 'rgba(0,0,0,0.78)',
      fontWeight: 600,
      ...extra
    };
  }
  let backgroundColor;
  try {
    backgroundColor = alpha(c, 0.14);
  } catch {
    backgroundColor = 'rgba(100,116,139,0.12)';
  }
  return { color: c, backgroundColor, fontWeight: 600, ...extra };
}

const semantic = (fg, bg) => ({
  color: `var(${fg})`,
  backgroundColor: `var(${bg})`,
  fontWeight: 600
});

// Deterministic, well-spread hue (0–359) for a tag label. The ×137 golden-angle
// step throws visually-similar labels ("Tag 3"/"Tag 4"/"Tag 5") ~137° apart on
// the colour wheel, so sequential tags never collapse to one near-identical
// shade the way the stored `getColor` pastels do.
function hueForLabel(label) {
  const s = String(label || '');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) & 0x7fffffff;
  }
  return (hash * 137) % 360;
}

// A generic tag's tone: DEEP text on a LIGHT tint of the SAME hue — the readable
// "Imported from …" purple model, applied per-tag. bg L95/S50 + fg L24/S60 keeps
// every hue ≥6:1 contrast (WCAG AA is 4.5:1), so tags stay legible for low-vision
// and colour-blind users regardless of which hue the label hashes to.
export function labelTagStyle(label, extra = {}) {
  const h = hueForLabel(label);
  return {
    color: `hsl(${h}, 60%, 24%)`,
    backgroundColor: `hsl(${h}, 50%, 95%)`,
    fontWeight: 600,
    ...extra
  };
}

/**
 * Canonical tag styling shared by the pedigree/family tables AND the pedigree
 * sidebar tag chips, so a given tag looks the same everywhere. Well-known tags
 * get a fixed semantic colour; everything else gets a per-label deep-on-light
 * tone (unique hue + AA contrast) via labelTagStyle.
 *   - "Imported from …" → purple    - "Preclinic"/clinic/hospital → green
 *   - "Migrated Family"/migration → teal   - merge → amber   - study → blue
 *
 * `color` (the stored per-tag `tagColor`) is intentionally ignored for generic
 * tags: it is an auto-generated `getColor` pastel that is both near-identical
 * across tags and too faint to read, so we derive a unique, readable colour from
 * the label instead — no data migration needed to fix existing tags.
 */
// eslint-disable-next-line no-unused-vars
export function resolveTagStyle(label, color, extra = {}) {
  const l = (label || '').toLowerCase();
  if (l.startsWith('imported from phenopacket'))
    return { backgroundColor: '#c8a8e9', color: '#3b1f5e', fontWeight: 600, ...extra };
  if (l.startsWith('imported from'))
    return {
      ...semantic('--color-tag-import-fg', '--color-tag-import-bg'),
      ...extra
    };
  if (l.includes('migrat')) return tagChipStyle('#2ba9bf', extra); // teal
  if (l.includes('merge') || l.includes('merged'))
    return {
      ...semantic('--color-tag-merge-fg', '--color-tag-merge-bg'),
      ...extra
    };
  if (l.includes('clinic') || l.includes('hospital'))
    return {
      ...semantic('--color-tag-clinic-fg', '--color-tag-clinic-bg'),
      ...extra
    };
  if (l.includes('study') || l.includes('research'))
    return {
      ...semantic('--color-tag-study-fg', '--color-tag-study-bg'),
      ...extra
    };
  return labelTagStyle(label, extra);
}
